import { Box, Typography, Menu, MenuItem, useTheme, SxProps, Theme, Checkbox } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState } from 'react';
import { WorkRestProfile, WorkRestIntensity, WorkRestModifier } from '../../../Services/API/workRestApi';
import { getIntensityActions, getModifierActions } from './WorkRestHelpers';
import { PulseIcon } from '../../../Assets/WorkRest/Pulse';
import { ShieldIcon } from '../../../Assets/WorkRest/Shield';
import { SplitScreenAddIcon } from '../../../Assets/WorkRest/SplitScreenAdd';

interface Props {
  workRestProfile?: WorkRestProfile;
  currentIntensity?: WorkRestIntensity;
  currentModifiers: WorkRestModifier[];
  handleIntensityChange: (intensityId: number) => void;
  handleModifierChange: (modifierId: number) => void;
  totalAdjustment: number;
}

export default function WorkRestAdjustmentsCardWeb({
  workRestProfile,
  currentIntensity,
  currentModifiers,
  handleIntensityChange,
  handleModifierChange,
  totalAdjustment,
}: Props) {
  const theme = useTheme();
  const [modifierAnchorEl, setModifierAnchorEl] = useState<null | HTMLElement>(null);
  const [intensityAnchorEl, setIntensityAnchorEl] = useState<null | HTMLElement>(null);

  const modifierOpen = Boolean(modifierAnchorEl);
  const intensityOpen = Boolean(intensityAnchorEl);

  const modifierActions = getModifierActions(workRestProfile, currentModifiers);
  const intensityActions = getIntensityActions(workRestProfile, currentIntensity);

  const totalAdjustmentLabel = `${totalAdjustment >= 0 ? '+' : ''}${totalAdjustment.toFixed(1)}°F`;

  const ExpandIcon = () => (
    <Box display="flex" flexDirection="column" alignItems="center" ml={1}>
      <ExpandLess sx={{ color: '#8B929E', fontSize: 18, m: 0, p: 0 }} />
      <ExpandMore sx={{ color: '#8B929E', fontSize: 18, m: 0, p: 0, mt: '-10px' }} />
    </Box>
  );

  const SelectAdjustments = (
    anchorEl: HTMLElement | null,
    open: boolean,
    onClose: () => void,
    actions: { name: string; title: string, id: number }[],
    onSelect: (id: number) => void,
    selectedNames: string[] = [],
    twoLines = true,
    closeOnSelect = false,
    Sx?: SxProps<Theme>
  ) => (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose} sx={{ '& .MuiMenu-list': { padding: 0, backgroundColor: theme.palette.background.default } }}>
      {actions.map((action) => {
        const isSelected = selectedNames.includes(action.name);
        return (
          <MenuItem
            key={action.name}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(action.id);
              if (closeOnSelect) onClose();
            }}
            selected={isSelected}
            sx={Sx}
          >
            {twoLines ? (
              <Box display="flex" flexDirection="row" alignItems="start" width="100%">
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(action.id);
                    if (closeOnSelect) onClose();
                  }}
                  disableRipple
                  size="small"
                  sx={{ mr: 1, p: 0.5 }}
                />
                <Box display="flex" flexDirection="column">
                  <Typography>{action.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {action.title}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography width="100%">{action.title}</Typography>
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );
  
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <SplitScreenAddIcon color={theme.palette.text.primary}/>
        <Typography fontSize={18} fontWeight="bold">
          Adjustments
        </Typography>
      </Box>
      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <ShieldIcon width={23} height={23} />
          <Typography variant="body1">Clothing / PPE</Typography>
        </Box>
        <Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => setModifierAnchorEl(e.currentTarget)}
          >
            <Typography variant="body1" color="#8B929E">
              {totalAdjustmentLabel}
            </Typography>
            {ExpandIcon()}
          </Box>
          {SelectAdjustments(
            modifierAnchorEl,
            modifierOpen,
            () => setModifierAnchorEl(null),
            modifierActions,
            handleModifierChange,
            currentModifiers.map((m) => m.name),
            true,
            false,
            {
              '&:hover': {
                backgroundColor: theme.palette.text.disabled,
              }
            }
          )}
        </Box>
      </Box>

      <Box mt={2} mb={2} height="1px" bgcolor="#626B7761" />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={2}>
          <PulseIcon width={20} height={20} />
          <Typography variant="body1">Intensity</Typography>
        </Box>
        <Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => setIntensityAnchorEl(e.currentTarget)}
          >
            <Typography variant="body1" color="#8B929E">
              {currentIntensity?.name}
            </Typography>
            {ExpandIcon()}
          </Box>
          {SelectAdjustments(
            intensityAnchorEl,
            intensityOpen,
            () => setIntensityAnchorEl(null),
            intensityActions,
            handleIntensityChange,
            currentIntensity ? [currentIntensity.name] : [],
            false,
            true,
            {
              '&:hover': {
                backgroundColor: theme.palette.action.hoverSecondary,
              },
              backgroundColor: theme.palette.background.default,
              '&.Mui-selected': {
                backgroundColor: theme.palette.mode === 'dark' ? '#26404d' : '#f3f8fb',
              }
            }
          )}
        </Box>
      </Box>
    </Box>
  );
}
